import { Box, Button, Flex, IconFont, Input, Select, Text } from '@byecode/ui'
import type { CustomStatisticMethod, DataSourceAbstract } from '@lighthouse/core'
import { DataSourceType, FilterMode } from '@lighthouse/core'
import {
    CUSTOM_STATISTIC_METHOD_OPTIONS,
    FieldTypeTag,
    getAppointField,
    getFieldIcon,
    getIsFindUseInFilter,
    getViewColumns,
    isStatisticMethodAllowed,
    ListItem4ByecodeUi,
    notDataSettingInnerTypes,
    SelectDataSource,
    useFindUseObjectContext,
    ViewFieldFilterConfigure
} from '@lighthouse/shared'
import React, { useCallback, useMemo } from 'react'
import { Controller, useFormContext, useWatch } from 'react-hook-form'

import { ListItemMenu } from '@/components/BlockSettings/Common/ListItemMenu'
import { useCurrentAppId, useCurrentEnvId } from '@/hooks/useApplication'
import { useDataSourceList } from '@/hooks/useDataSource'
import { useIsDisabledWithVersion } from '@/hooks/useIsDisabledWithVersion'

interface StatisticFieldItemProps {
    prefixName: string
    viewDataSource: DataSourceAbstract
    filterMenuOpened?: boolean
    onFilterMenuOpenedChange?: (opened: boolean) => void
    onRemove: () => void
}

export const StatisticFieldItem: React.FC<StatisticFieldItemProps> = ({
    prefixName,
    viewDataSource,
    filterMenuOpened,
    onFilterMenuOpenedChange,
    onRemove
}) => {
    const { control, setValue } = useFormContext()
    const findUseObject = useFindUseObjectContext()
    const disabledWithVersion = useIsDisabledWithVersion()
    const appId = useCurrentAppId()
    const envId = useCurrentEnvId()
    const dataSourceList = useDataSourceList(appId, envId)
    const ordinaryDataSourceList = useMemo(
        () => dataSourceList.filter(item => item.type === DataSourceType.dataSource),
        [dataSourceList]
    )

    const dsId = useWatch({ control, name: `${prefixName}.dsId` }) as string | undefined
    const targetFieldId = useWatch({ control, name: `${prefixName}.targetFieldId` }) as string | undefined
    const method = useWatch({ control, name: `${prefixName}.method` }) as CustomStatisticMethod | undefined

    const targetDataSource = useMemo(() => ordinaryDataSourceList.find(item => item.id === dsId), [dsId, ordinaryDataSourceList])
    const targetFields = useMemo(() => (targetDataSource ? Object.values(targetDataSource.schema) : []), [targetDataSource])
    const targetField = useMemo(() => targetFields.find(item => item.id === targetFieldId), [targetFieldId, targetFields])

    const targetFieldOptions = useMemo(
        () =>
            targetFields.map(field => ({
                label: field.name,
                value: field.id,
                icon: getFieldIcon(field.id, field.type, field.innerType),
                extra: <FieldTypeTag type={field.type} innerType={field.innerType} />
            })),
        [targetFields]
    )

    const methodOptions = useMemo(() => {
        if (!targetField) {
            return []
        }
        return CUSTOM_STATISTIC_METHOD_OPTIONS.filter(item => item.innerTypeList.includes(targetField.innerType || 'NULL'))
    }, [targetField])

    const viewColumns = useMemo(() => {
        const {
            schema,
            viewOptions: { tableProps }
        } = viewDataSource
        return getViewColumns({ tableProps, value: [], schema })
    }, [viewDataSource])

    const primaryField = useMemo(() => getAppointField(viewDataSource, 'ID')?.id, [viewDataSource])

    const handleChangeDataSource = useCallback(
        (value: string, onChange: (value: string) => void) => {
            onChange(value)
            setValue(`${prefixName}.targetFieldId`, '')
            setValue(`${prefixName}.method`, undefined)
            setValue(`${prefixName}.filter`, undefined)
        },
        [prefixName, setValue]
    )

    const handleChangeTargetField = useCallback(
        (value: string, onChange: (value: string) => void) => {
            onChange(value)
            const nextField = targetFields.find(item => item.id === value)
            if (!isStatisticMethodAllowed(method, nextField?.innerType)) {
                setValue(`${prefixName}.method`, undefined)
            }
        },
        [method, prefixName, setValue, targetFields]
    )

    return (
        <Box
            style={{
                border: '1px solid var(--color-gray-200)',
                borderRadius: 8,
                padding: 8,
                display: 'flex',
                flexDirection: 'column',
                gap: 4
            }}
        >
            <Flex alignItems="center" justifyContent="space-between" px={8}>
                <Controller
                    control={control}
                    name={`${prefixName}.name`}
                    render={({ field }) => (
                        <Input
                            defaultValue={field.value}
                            placeholder="显示名称"
                            style={{ flex: 1 }}
                            disabled={disabledWithVersion}
                            onKeyDown={e => {
                                if (e.code === 'Enter') {
                                    field.onChange(e.currentTarget.value)
                                    e.currentTarget.blur()
                                }
                            }}
                            onBlur={e => field.onChange(e.currentTarget.value)}
                        />
                    )}
                />
                <Button type="text" onClick={onRemove} disabled={disabledWithVersion} style={{ marginLeft: 4 }}>
                    <IconFont type="Trash" size={16} fill="var(--color-gray-400)" />
                </Button>
            </Flex>

            <ListItem4ByecodeUi alignItems="center" justifyContent="space-between">
                <Text>数据表</Text>
                <Controller
                    control={control}
                    name={`${prefixName}.dsId`}
                    render={({ field }) => (
                        <SelectDataSource
                            withinPortal
                            placeholder="请选择数据表"
                            value={field.value}
                            onChange={value => handleChangeDataSource(value, field.onChange)}
                            searchable
                            width={180}
                            disabled={disabledWithVersion}
                            dataSourceList={ordinaryDataSourceList}
                        />
                    )}
                />
            </ListItem4ByecodeUi>

            <ListItem4ByecodeUi alignItems="center" justifyContent="space-between">
                <Text>目标统计字段</Text>
                <Controller
                    control={control}
                    name={`${prefixName}.targetFieldId`}
                    render={({ field }) => (
                        <Select
                            style={{ width: 180 }}
                            placeholder="请选择字段"
                            options={targetFieldOptions}
                            value={field.value}
                            onChange={value => handleChangeTargetField(value, field.onChange)}
                            searchable
                            disabled={disabledWithVersion || !dsId}
                        />
                    )}
                />
            </ListItem4ByecodeUi>

            <ListItem4ByecodeUi alignItems="center" justifyContent="space-between">
                <Text>统计方法</Text>
                <Controller
                    control={control}
                    name={`${prefixName}.method`}
                    render={({ field }) => (
                        <Select
                            style={{ width: 180 }}
                            placeholder="请选择方法"
                            options={methodOptions}
                            value={field.value}
                            onChange={field.onChange}
                            disabled={disabledWithVersion || !targetFieldId}
                        />
                    )}
                />
            </ListItem4ByecodeUi>

            <Controller
                control={control}
                name={`${prefixName}.filter`}
                render={({ field: { onChange, value } }) => {
                    const filtersLength = value?.expression?.conditions?.length || 0
                    const isHighLight = getIsFindUseInFilter({
                        findUseObject,
                        data: value,
                        currentDsId: dsId
                    })
                    return (
                        <ListItemMenu
                            width="auto"
                            highlighting={isHighLight}
                            icon={<IconFont size={16} type="Filter" />}
                            label="筛选字段"
                            opened={filterMenuOpened}
                            onChange={onFilterMenuOpenedChange}
                            outsidePress={event => {
                                const el = event.target as HTMLElement | null
                                return !el?.closest?.('[role="listbox"]')
                            }}
                            value={Boolean(filtersLength) && `${String(filtersLength)} 条筛选项`}
                            popupContent={
                                targetDataSource ? (
                                    <ViewFieldFilterConfigure
                                        dataSourceList={dataSourceList}
                                        columns={viewColumns}
                                        primaryField={primaryField}
                                        viewDsId={viewDataSource.id}
                                        viewType="custom"
                                        paramsDataSource={targetDataSource}
                                        filter={value}
                                        fieldMode={FilterMode.CUSTOM}
                                        paramsMode={FilterMode.CUSTOM}
                                        noSettingInnerType={notDataSettingInnerTypes}
                                        disabled={disabledWithVersion}
                                        onFilter={onChange}
                                    />
                                ) : (
                                    <Box p={12}>
                                        <Text color="var(--color-gray-400)">请先选择数据表</Text>
                                    </Box>
                                )
                            }
                        />
                    )
                }}
            />
        </Box>
    )
}
